import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthState } from 'react-redux-django-auth';
import { ROLES, DASHBOARD_ROUTES, RoleType } from '../../constants/roles';
import { GraduationCap, UserCheck, ShieldCheck, Crown } from 'lucide-react';
import axios from 'axios';

interface PortalOption {
  role: RoleType;
  label: string;
  icon: React.ElementType;
  path: string;
  prefix: string;
}

const ALL_PORTALS: PortalOption[] = [
  {
    role: ROLES.STUDENT,
    label: 'Student',
    icon: GraduationCap,
    path: DASHBOARD_ROUTES[ROLES.STUDENT],
    prefix: '/student/dashboard',
  },
  {
    role: ROLES.INSTRUCTOR,
    label: 'Instructor',
    icon: UserCheck,
    path: DASHBOARD_ROUTES[ROLES.INSTRUCTOR],
    prefix: '/instructor/dashboard',
  },
  {
    role: ROLES.ADMIN,
    label: 'Admin',
    icon: ShieldCheck,
    path: DASHBOARD_ROUTES[ROLES.ADMIN],
    prefix: '/staff/dashboard',
  },
  {
    role: ROLES.SUPERADMIN,
    label: 'SuperAdmin',
    icon: Crown,
    path: DASHBOARD_ROUTES[ROLES.SUPERADMIN],
    prefix: '/superadmin/dashboard',
  },
];

export const RoleSwitcher: React.FC = () => {
  const { user: rawUser } = useAuthState();
  const user = (rawUser as any)?.user || (rawUser as any)?.data?.user || rawUser;
  const location = useLocation();

  if (!user) return null;

  const isSuperuser = Boolean(user.is_superuser || user.role === "SUPERADMIN");
  const rawRoles: RoleType[] = Array.isArray(user.roles) && user.roles.length > 0
    ? user.roles
    : [user.role || (isSuperuser ? ROLES.SUPERADMIN : user.is_staff ? ROLES.ADMIN : ROLES.STUDENT)];

  const userRoles = rawRoles.map((r: string) => r.toUpperCase() as RoleType);

  // Only render switcher if the user has more than 1 role or is a superuser
  if (!isSuperuser && userRoles.length <= 1) {
    return null;
  }

  // Filter portals user is authorized to visit
  const availablePortals = ALL_PORTALS.filter((portal) =>
    isSuperuser || userRoles.includes(ROLES.SUPERADMIN) || userRoles.includes(portal.role)
  );

  const handleSwitch = async (targetRole: RoleType) => {
    try {
      const token = localStorage.getItem("access");
      const apiURL = process.env.REACT_APP_API_URL || "";
      await axios.post(
        `${apiURL}/course/switch-active-role/`,
        { role: targetRole },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `JWT ${token}` } : {}),
          },
        }
      );
    } catch (err) {
      console.error("Failed to persist active role switch on backend", err);
    }
  };

  return (
    <div className="cht-hidden md:cht-flex cht-items-center cht-gap-1 cht-bg-gray-100/90 cht-p-1 cht-rounded-xl cht-border cht-border-gray-200/50 cht-shadow-sm">
      <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-px-2">
        Portal:
      </span>
      {availablePortals.map((portal) => {
        const Icon = portal.icon;
        const isActive = location.pathname.startsWith(portal.prefix);

        return (
          <Link
            key={portal.role}
            to={portal.path}
            onClick={() => handleSwitch(portal.role)}
            className={`cht-flex cht-items-center cht-gap-1.5 cht-px-2.5 cht-py-1 cht-rounded-lg cht-text-xs cht-font-bold cht-transition-all cht-duration-200 ${
              isActive
                ? 'cht-bg-[#3e57da] cht-text-white cht-shadow-sm'
                : 'cht-text-gray-600 hover:cht-text-gray-900 hover:cht-bg-white/60'
            }`}
          >
            <Icon size={13} className={isActive ? 'cht-text-white' : 'cht-text-gray-500'} />
            <span>{portal.label}</span>
          </Link>
        );
      })}
    </div>
  );
};

export default RoleSwitcher;
